var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkHeading } from 'uitk-react-text';
import { UitkSpacing } from 'uitk-react-spacing';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import { NoMatchesFound } from '@shared-ui/retail-activities-no-matches-found';
import { ActivityGroupViewType, ActivityMessageType } from '../../../common/__generated__/api';
import { useStores } from '../../../utils/hooks';
import { getPageId } from '../../../helpers/common-helper';
import { onClickScrollToTarget } from '../../../helpers/smooth-scroll';
import { ActivityCarousel, ActivityTextTile, DestinationActivityList, PlaceholderCards, SlimCategoryCarousel } from './module-components';
import { ActivityMessageBanner, mapMessageTileToBannerProps, trackAction } from '../../common/banners';
import { ActivitySpannedTileList } from './module-components/activity-spanned-tile-list';
export var DestinationView = function () {
  var _a = useStores(),
    activityDetailStore = _a.activityDetailStore,
    analytics = _a.analytics,
    activitySearchStore = _a.activitySearchStore,
    searchCriteriaStore = _a.searchCriteriaStore;
  var loadingActivities = activitySearchStore.loadingActivities,
    activityGroups = activitySearchStore.activityGroups,
    pageTitle = activitySearchStore.pageTitle,
    noMatchesFound = activitySearchStore.noMatchesFound;
  var formatText = useLocalization().formatText;
  var searchQuery = searchCriteriaStore.searchQuery;
  var regionName = searchQuery.location,
    regionId = searchQuery.rid;
  var pageId = getPageId(activitySearchStore);
  React.useEffect(function () {
    var groups = activitySearchStore.activityGroups;
    loadingActivities ? activityDetailStore.resetDetailsPage() : analytics.markPageUsable({});
    groups.forEach(function (group) {
      var _a;
      var viewType = group.viewType,
        activityTiles = group.activityTiles,
        analyticsList = group.analyticsList;
      if (analyticsList.length > 0) {
        analyticsList.forEach(function (analyticsObj) {
          analytics.trackEvent(analyticsObj.referrerId, analyticsObj.linkName, analytics.omnitureData, null, true);
        });
      }
      if (viewType === ActivityGroupViewType.MESSAGE && activityTiles) {
        var sourcedCampaignMsg = activityTiles.find(function (activityTile) {
          return activityTile.type === ActivityMessageType.SOURCED_CAMPAIGN;
        });
        if ((_a = sourcedCampaignMsg === null || sourcedCampaignMsg === void 0 ? void 0 : sourcedCampaignMsg.analytics) === null || _a === void 0 ? void 0 : _a.referrerId) {
          trackAction(sourcedCampaignMsg.analytics.referrerId, sourcedCampaignMsg.analytics.linkName, analytics, []);
        }
        if (searchCriteriaStore.activityToFocus.element) {
          searchCriteriaStore.activityToFocus.element.focus();
          searchCriteriaStore.activityToFocus.element = null;
          searchCriteriaStore.activityToFocus.index = null;
        }
        if (searchCriteriaStore.scrollPosition) {
          onClickScrollToTarget({
            scrollTo: searchCriteriaStore.scrollPosition
          });
          searchCriteriaStore.scrollPosition = 0;
        }
      }
    });
  }, []);
  var getMessageBanners = function (messages) {
    return messages.map(function (message, index) {
      return React.createElement(ActivityMessageBanner, __assign({
        key: index
      }, mapMessageTileToBannerProps(message)));
    });
  };
  var getCarousel = function (activityGroup, index) {
    switch (activityGroup.viewType) {
      case ActivityGroupViewType.SLIM_CATEGORY_TILES_CAROUSEL:
        return React.createElement(SlimCategoryCarousel, {
          key: "destination-group-".concat(index),
          categoryGroup: activityGroup
        });
      case ActivityGroupViewType.ACTIVITY_TILES_CAROUSEL:
        return React.createElement(ActivityCarousel, {
          key: "destination-group-".concat(index),
          activityGroup: activityGroup,
          regionId: regionId,
          regionName: regionName,
          searchCriteriaStore: searchCriteriaStore,
          pageId: pageId
        });
      case ActivityGroupViewType.MESSAGE:
        return React.createElement(UitkSpacing, {
          margin: {
            block: 'six'
          }
        }, React.createElement("div", {
          key: "destination-group-".concat(index)
        }, getMessageBanners(activityGroup.activityTiles)));
      case ActivityGroupViewType.TEXT:
        return React.createElement(ActivityTextTile, {
          key: "destination-group-".concat(index),
          activityTiles: activityGroup.activityTiles
        });
      case ActivityGroupViewType.ACTIVITY_SPANNED_TILE_LIST:
        return React.createElement(ActivitySpannedTileList, {
          key: "destination-group-".concat(index),
          activityTiles: activityGroup.activityTiles
        });
      case ActivityGroupViewType.ACTIVITY_LIST:
        return React.createElement(DestinationActivityList, {
          key: "destination-group-".concat(index),
          activityGroup: activityGroup,
          activityTiles: activityGroup.activityTiles,
          index: index
        });
      default:
        return React.createElement(React.Fragment, null);
    }
  };
  var renderPageTitle = function (size) {
    return React.createElement(UitkSpacing, {
      margin: {
        blockend: 'six'
      }
    }, React.createElement(UitkHeading, {
      tag: "h2",
      size: size,
      "data-testid": "activity-srp-page-title"
    }, pageTitle));
  };
  return loadingActivities ? React.createElement(UitkSpacing, {
    padding: {
      inline: 'three'
    }
  }, React.createElement("section", {
    id: "activity-cards",
    "data-testid": "destination-view-page--loading"
  }, React.createElement(PlaceholderCards, {
    cardsCount: 8
  }))) : React.createElement("section", {
    id: "placeholder",
    "data-testid": "destination-view-page"
  }, React.createElement(UitkHeading, {
    tag: "h2",
    className: "is-visually-hidden",
    tabIndex: -1
  }, formatText('lx.search.accessibility.heading')), pageTitle && React.createElement(Viewport, null, React.createElement(ViewSmall, null, renderPageTitle(4)), React.createElement(ViewLarge, null, renderPageTitle(3))), noMatchesFound && React.createElement("div", {
    "data-testid": "activity-search--no-matches-found"
  }, React.createElement(NoMatchesFound, {
    data: noMatchesFound
  })), activityGroups.map(function (activityGroup, index) {
    if (index === 0 && activityGroup.viewType === ActivityGroupViewType.TEXT) {
      return null;
    }
    return getCarousel(activityGroup, index);
  }));
};